import { compact, sortBy, take } from "lodash";

import {
  CompanyRoleIdWithName,
  DetailsPanel,
  DetailsPanelRef,
  DocumentItem,
} from "@/dbot/common/commonDomain";
import { BoardMeetingVoting } from "./boardMeetingVoting";
import { CompanyRole, CompanyroleId } from "@/types/CompanyInfo";
import { RoleType } from "@/types/enums/RoleType";
import { getFullName, isBoardMember, isChairperson, isDeputyChairperson } from "@/utils/Roles";
import {
  boardMeetingParticipantsLabel,
  boardMeetingSignatoriesLabel,
  ExtraParticipant,
  ProjectCompanyRole,
  projectCompanyRoleLabel,
} from "@/dbot/common/commonBoardMeetingDomain";
import {
  BoardMeetingForm,
  NoticeOfBoardMeetingCustomPanel,
} from "@/dbot/noticeBoardMeeting/noticeBoardMeetingDomain";
import { formatDateAndTime } from "@/i18n/dateFormat";
import { Language } from "@/i18n/languages";

import { TMessage } from "@/i18n/tt";
import { DocumentType } from "@/common/documents";
import { MeetingPlace } from "@/meetings/meetingsDomain";
import {
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";
import { LocalizedText } from "@/common/common";

export enum SignersMode {
  ALL = "ALL",
  SELECTED = "SELECTED",
}

export type BoardMeetingMinutes = {
  type: DocumentType.board_meeting;
  boardMeetingForm?: BoardMeetingForm;
  meetingPlace?: MeetingPlace;
  inlineAddress?: string;
  selectedSigningBoardMembers?: CompanyRoleIdWithName[];
  optionalSignatories: CompanyroleId[];
  extraSignatories: ExtraParticipant[];
  meetingChair?: CompanyRoleIdWithName;
  incompetentBoardMembers?: Record<string, string[]>;
  boardMeetingVotes: BoardMeetingVoting[];
  deadlineForNotes?: string;
  numberOfBoardMembers: number;
  documentLanguage: Language[];
  physicalMeetingSignersMode: SignersMode;
  board: ProjectCompanyRole[];
  participants: CompanyroleId[];
  extraParticipants: ExtraParticipant[];
  additionalDetailsTexts?: LocalizedText[];
  additionalParticipantDetailsTexts?: LocalizedText[];
};

export const boardMeetingMinutes = (
  data: Partial<BoardMeetingMinutes> = {},
): BoardMeetingMinutes => ({
  type: DocumentType.board_meeting,
  boardMeetingForm: data.boardMeetingForm,
  selectedSigningBoardMembers: data.selectedSigningBoardMembers,
  optionalSignatories: data.optionalSignatories ?? [],
  extraSignatories: data.extraSignatories ?? [],
  meetingChair: data.meetingChair,
  incompetentBoardMembers: data.incompetentBoardMembers,
  boardMeetingVotes: data.boardMeetingVotes ?? [],
  deadlineForNotes: data.deadlineForNotes,
  numberOfBoardMembers: data.numberOfBoardMembers ?? 0,
  documentLanguage: data.documentLanguage ?? [Language.NO],
  physicalMeetingSignersMode: data.physicalMeetingSignersMode ?? SignersMode.ALL,
  board: data.board ?? [],
  participants: data.participants ?? [],
  extraParticipants: data.extraParticipants ?? [],
  additionalDetailsTexts: data.additionalDetailsTexts ?? [],
  additionalParticipantDetailsTexts: data.additionalParticipantDetailsTexts ?? [],
});

export type BoardMemberOption = {
  companyroleId: number;
  name: string;
  role?: RoleType;
};

export const supportsSelectionOfSigners = (roles: { role?: RoleType }[]) =>
  roles.filter(isBoardMember).length >= 5;

export const isSelectionOfSignersEnabled = (meetingForm: BoardMeetingForm) =>
  meetingForm !== BoardMeetingForm.CIRCULATION;

export function getDefaultSigners(
  presentBoardMembers: CompanyRoleIdWithName[],
  defaultTwoSigners: CompanyRoleIdWithName[],
  boardMeetingForm: BoardMeetingForm,
  physicalMeetingSignersMode: SignersMode,
): CompanyRoleIdWithName[] {
  switch (boardMeetingForm) {
    case BoardMeetingForm.CIRCULATION:
    case BoardMeetingForm.ELECTRONIC:
    case BoardMeetingForm.HYBRID:
    case BoardMeetingForm.PHYSICAL:
      return physicalMeetingSignersMode === SignersMode.ALL
        ? presentBoardMembers
        : defaultTwoSigners;
  }
}

export function getDefaultTwoSigners(
  presentBoardMembers: CompanyRoleIdWithName[],
  chairperson?: CompanyRoleIdWithName,
  deputyChairperson?: CompanyRoleIdWithName,
  selectedMeetingChair?: CompanyRoleIdWithName,
): CompanyRoleIdWithName[] {
  const presentIds = new Set(presentBoardMembers.map((m) => m.companyroleId));
  const isChairpersonPresent = chairperson && presentIds.has(chairperson.companyroleId);
  const isDeputyChairpersonPresent =
    deputyChairperson && presentIds.has(deputyChairperson.companyroleId);
  const isSelectedMeetingChairPresent =
    selectedMeetingChair && presentIds.has(selectedMeetingChair.companyroleId);

  if (isChairpersonPresent && isDeputyChairpersonPresent) {
    return [chairperson!, deputyChairperson!];
  } else if (isChairpersonPresent) {
    return [chairperson!, ...take(withoutMember(presentBoardMembers, chairperson), 1)];
  } else if (isDeputyChairpersonPresent) {
    return [deputyChairperson!, ...take(withoutMember(presentBoardMembers, deputyChairperson), 1)];
  } else if (isSelectedMeetingChairPresent) {
    return [
      selectedMeetingChair!,
      ...take(withoutMember(presentBoardMembers, selectedMeetingChair), 1),
    ];
  }
  return take(presentBoardMembers, 2);
}

function withoutMember(
  members: CompanyRoleIdWithName[],
  member?: CompanyRoleIdWithName,
): CompanyRoleIdWithName[] {
  return members.filter((m) => m.companyroleId !== member?.companyroleId);
}

export function incompetentBoardMembersFromJson(
  json?: Record<string, string[]>,
): Map<number, Set<string>> {
  if (!json) {
    return new Map();
  }
  return new Map(Object.entries(json).map(([key, val]) => [Number(key), new Set(val)]));
}

export function incompetentBoardMembersToJson(
  m: Map<number, Set<string>>,
): Record<string, string[]> {
  return Object.fromEntries(
    Array.from(m.entries()).map(([companyroleId, decisionIds]) => [
      companyroleId,
      Array.from(decisionIds),
    ]),
  );
}

export function sortBoardMemberOptions(options: BoardMemberOption[]): BoardMemberOption[] {
  return sortBy(
    options,
    (r) => {
      switch (r.role) {
        case RoleType.CHAIRPERSON:
          return 1;
        case RoleType.DEPUTY_CHAIRPERSON:
          return 2;
        default:
          return 3;
      }
    },
    "name",
  );
}

export function boardMemberOptionLabel(option: BoardMemberOption): TMessage {
  return projectCompanyRoleLabel(option);
}

export function lookupBoardMemberOption(
  options: BoardMemberOption[],
  r: CompanyRoleIdWithName,
): BoardMemberOption {
  return options.find((o) => o.companyroleId === r.companyroleId) ?? r;
}

export function toBoardMemberOption(r: CompanyRole): BoardMemberOption {
  return {
    companyroleId: r.companyroleId,
    name: getFullName(r),
    role: r.role,
  };
}

export enum BoardMinutesCustomPanel {
  meetingDetails = "meetingDetails",
  participants = "participants",
  meetingChair = "meetingChair",
  signatories = "signatories",
}

function findDocument(documents: DocumentItem[], type: DocumentType) {
  return documents.find((d) => d.type === type);
}

function boardMeetingDocument(currentDocuments: DocumentItem[]): DocumentItem | undefined {
  return (
    findDocument(currentDocuments, DocumentType.notice_board_meeting) ??
    findDocument(currentDocuments, DocumentType.board_meeting)
  );
}

export function isRoleAttendingBoardMeeting(
  currentDocuments: DocumentItem[],
  companyroleId: CompanyroleId | undefined,
): boolean {
  const data = boardMeetingDocument(currentDocuments)?.documentData;
  if (
    companyroleId === undefined ||
    (data?.type !== DocumentType.notice_board_meeting && data?.type !== DocumentType.board_meeting)
  ) {
    return false;
  }
  return (data.participants ?? []).includes(companyroleId);
}

export function boardMeetingParticipantsPanel(
  currentDocuments: DocumentItem[],
): DetailsPanelRef | undefined {
  const boardMeetingDoc = boardMeetingDocument(currentDocuments);
  if (boardMeetingDoc === undefined) {
    return undefined;
  }
  return {
    type: "custom",
    id:
      boardMeetingDoc.type === DocumentType.notice_board_meeting
        ? NoticeOfBoardMeetingCustomPanel.recipients
        : BoardMinutesCustomPanel.participants,
    documentId: boardMeetingDoc.documentId,
  };
}

export function getCustomBoardMeetingPanels({
  documentId,
  meetingStartDate,
  meetingStartTime,
  meetingChair,
  physicalMeetingSignersMode,
  selectedSigningBoardMembers,
  board,
  presentBoardMembers,
  extraParticipants,
  locale,
}: {
  documentId: string;
  meetingStartDate: LocalDateFieldModel;
  meetingStartTime: TimeFieldModel;
  meetingChair: RadioFieldModel<BoardMemberOption | undefined>;
  physicalMeetingSignersMode: RadioFieldModel<SignersMode>;
  selectedSigningBoardMembers: OptionFieldModel<CompanyRoleIdWithName>;
  board: ProjectCompanyRole[];
  boardMeetingForm: RadioFieldModel<BoardMeetingForm>;
  presentBoardMembers: CompanyRoleIdWithName[];
  extraParticipants: ExtraParticipant[];
  locale: Language;
}): DetailsPanel[] {
  return compact([
    {
      type: "custom",
      id: BoardMinutesCustomPanel.meetingDetails,
      documentId,
      heading: { key: "dbot.meeting-details" },
      icon: "calendar",
      label: {
        translated: formatDateAndTime(
          locale,
          meetingStartDate.validatedValue,
          meetingStartTime.validatedValue,
        ),
      },
    },
    {
      type: "custom",
      id: BoardMinutesCustomPanel.participants,
      documentId,
      heading: { key: "dbot.participants" },
      icon: "users",
      label: boardMeetingParticipantsLabel({
        board: board,
        participants: presentBoardMembers.map((m) => m.companyroleId),
        extraParticipants: extraParticipants,
      }),
    },
    {
      type: "custom",
      id: BoardMinutesCustomPanel.meetingChair,
      documentId,
      heading: { key: "dbot.meeting-chair" },
      icon: "user",
      label: { translated: meetingChair.value?.name ?? "" },
    },
    {
      type: "custom",
      id: BoardMinutesCustomPanel.signatories,
      documentId,
      hasValidationError: !selectedSigningBoardMembers.isValid,
      heading: { key: "dbot.signatories" },
      icon: "signature",
      label: boardMeetingSignatoriesLabel({
        signersMode: physicalMeetingSignersMode.value,
        selectedSigningBoardMembers: selectedSigningBoardMembers.selected,
      }),
    },
  ]);
}

export function cannotSelectMeetingChairMessage({
  isChairpersonPresent,
  isDeputyChairpersonPresent,
}: {
  isChairpersonPresent: boolean;
  isDeputyChairpersonPresent: boolean;
}): TMessage | undefined {
  if (isChairpersonPresent) {
    return { key: "dbot.boardmeeting.chairperson-should-be-chair-when-present" };
  } else if (isDeputyChairpersonPresent) {
    return { key: "dbot.boardmeeting.deputy-chairperson-should-be-chair-when-present" };
  }
}

export function getMeetingChair({
  currentChair,
  presentMembers,
  board,
}: {
  currentChair?: { companyroleId: number };
  presentMembers: { companyroleId: number }[];
  board: ProjectCompanyRole[];
}): ProjectCompanyRole | undefined {
  const chairperson = board.find(isChairperson);
  const deputyChairperson = board.find(isDeputyChairperson);
  if (chairperson && presentMembers.some((m) => m.companyroleId === chairperson.companyroleId)) {
    return chairperson;
  } else if (
    deputyChairperson &&
    presentMembers.some((m) => m.companyroleId === deputyChairperson.companyroleId)
  ) {
    return deputyChairperson;
  } else if (
    currentChair &&
    presentMembers.some((m) => m.companyroleId === currentChair.companyroleId)
  ) {
    return board.find((b) => b.companyroleId === currentChair.companyroleId);
  }
  return board.find((b) => presentMembers.some((p) => p.companyroleId === b.companyroleId));
}
